import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Pagination,
  IconButton,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { logsApi } from '../services/api';

const LogsViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, checking: true });
  const [filters, setFilters] = useState({
    level: '',
    category: '',
    userId: '',
    search: '',
    startDate: '',
    endDate: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    levels: [],
    categories: [],
    userIds: [],
  });
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [expandedRows, setExpandedRows] = useState({});
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const data = await logsApi.getFilters();
      setFilterOptions(data);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: pagination.limit,
        skip: (pagination.page - 1) * pagination.limit,
      };

      // Add filters
      if (filters.level) params.level = filters.level;
      if (filters.category) params.category = filters.category;
      if (filters.userId) params.userId = filters.userId;
      if (filters.search) params.search = filters.search;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const data = await logsApi.getLogs(params);
      setLogs(data.logs || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to fetch logs';
      setError(errorMessage);
      console.error('Failed to fetch logs:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const data = await logsApi.getStats(params);
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [filters.startDate, filters.endDate]);

  // Check connection status
  const checkConnection = useCallback(async () => {
    try {
      const health = await logsApi.healthCheck();
      setConnectionStatus({
        connected: health.mongodb === 'connected',
        checking: false,
        logCount: health.logCount,
        error: null
      });
    } catch (err) {
      setConnectionStatus({
        connected: false,
        checking: false,
        error: err.message || 'Cannot connect to server. Make sure backend is running on port 3001.'
      });
      setError(err.message || 'Cannot connect to server');
    }
  }, []);

  // Initial load
  useEffect(() => {
    checkConnection();
    fetchFilterOptions();
  }, [checkConnection, fetchFilterOptions]);

  useEffect(() => {
    if (connectionStatus.connected) {
      fetchLogs();
      fetchStats();
    }
  }, [fetchLogs, fetchStats, connectionStatus.connected]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      level: '',
      category: '',
      userId: '',
      search: '',
      startDate: '',
      endDate: '',
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination(prev => ({ ...prev, page: value }));
  };

  const toggleRowExpand = (logId) => {
    setExpandedRows(prev => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const handleExportCSV = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      if (filters.level) params.level = filters.level;
      if (filters.category) params.category = filters.category;
      if (filters.userId) params.userId = filters.userId;
      if (filters.search) params.search = filters.search;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      await logsApi.exportToCSV(params);
    } catch (err) {
      setError(err.message || 'Failed to export logs to CSV');
      console.error('Failed to export CSV:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setDeleting(true);
      setError(null);
      
      const params = {};
      if (filters.level) params.level = filters.level;
      if (filters.category) params.category = filters.category;
      if (filters.userId) params.userId = filters.userId;
      if (filters.search) params.search = filters.search;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;

      const result = await logsApi.deleteAllLogs(params);
      
      setDeleteDialogOpen(false);
      setError(null);
      
      // Refresh logs and stats
      await fetchLogs();
      await fetchStats();
      
      // Show success message
      alert(`Successfully archived ${result.deletedCount || result.archivedCount || 0} log(s). Statistics are preserved.`);
    } catch (err) {
      setError(err.message || 'Failed to delete logs');
      console.error('Failed to delete logs:', err);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'error':
      case 'fatal':
        return 'error';
      case 'warn':
        return 'warning';
      case 'info':
        return 'info';
      case 'debug':
        return 'default';
      default:
        return 'default';
    }
  };

  // Helper function to extract userId from log entry
  const getUserId = (log) => {
    // First check root level
    if (log.userId) {
      return log.userId;
    }
    // Then check metadata
    if (log.metadata?.userId) {
      return log.metadata.userId;
    }
    // Check other possible metadata fields
    if (log.metadata?.user?.id) {
      return log.metadata.user.id;
    }
    if (log.metadata?.user?.userId) {
      return log.metadata.user.userId;
    }
    return 'N/A';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Logs Viewer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Monitor user logs and system activity
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {connectionStatus.checking ? (
              <Chip label="Checking connection..." color="default" icon={<CircularProgress size={16} />} />
            ) : connectionStatus.connected ? (
              <Chip 
                label={`Database Connected (${connectionStatus.logCount || 0} logs)`} 
                color="success" 
              />
            ) : (
              <Chip 
                label="MongoDB Disconnected" 
                color="error"
                onClick={checkConnection}
                sx={{ cursor: 'pointer' }}
              />
            )}
            <Button
              variant="outlined"
              color="primary"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCSV}
              disabled={loading || !connectionStatus.connected}
            >
              Export to CSV
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteDialogOpen(true)}
              disabled={loading || !connectionStatus.connected}
            >
              Archive All Logs
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Logs
                </Typography>
                <Typography variant="h4">
                  {stats.byLevel?.reduce((sum, item) => sum + item.count, 0) || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          {stats.byLevel?.map((stat) => (
            <Grid item xs={12} md={3} key={stat._id}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    {stat._id?.toUpperCase() || 'Unknown'}
                  </Typography>
                  <Typography variant="h4">{stat.count}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <IconButton onClick={() => setFiltersExpanded(!filtersExpanded)}>
            {filtersExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={filtersExpanded}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Level</InputLabel>
                <Select
                  value={filters.level}
                  label="Level"
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.levels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  label="Category"
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>User ID</InputLabel>
                <Select
                  value={filters.userId}
                  label="User ID"
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  {filterOptions.userIds.slice(0, 50).map((userId) => (
                    <MenuItem key={userId} value={userId}>
                      {userId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Start Date"
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="End Date"
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Search in messages..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={fetchLogs}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button variant="outlined" onClick={handleClearFilters}>
                  Clear Filters
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportCSV}
                  disabled={loading || !connectionStatus.connected}
                >
                  Export to CSV
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={loading || !connectionStatus.connected}
                >
                  Archive All Logs
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Collapse>
      </Paper>

      {/* Connection Error Alert */}
      {!connectionStatus.checking && !connectionStatus.connected && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }} 
          action={
            <Button color="inherit" size="small" onClick={checkConnection}>
              Retry
            </Button>
          }
        >
          <Typography variant="subtitle2" gutterBottom>
            Cannot connect to MongoDB
          </Typography>
          <Typography variant="body2">
            {connectionStatus.error || 'Please check if the backend server is running on port 3001 and MongoDB is accessible.'}
          </Typography>
        </Alert>
      )}

      {/* Error Alert */}
      {error && connectionStatus.connected && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Logs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>Timestamp</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Message</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No logs found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <React.Fragment key={log._id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggleRowExpand(log._id)}
                        >
                          {expandedRows[log._id] ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        {log.timestamp
                          ? format(new Date(log.timestamp), 'yyyy-MM-dd HH:mm:ss')
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={log.level?.toUpperCase() || 'UNKNOWN'}
                          color={getLevelColor(log.level)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{log.category || 'N/A'}</TableCell>
                      <TableCell>{getUserId(log)}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {log.message || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    {expandedRows[log._id] && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: 'grey.50', py: 2 }}>
                          <Box sx={{ pl: 4 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Full Details
                            </Typography>
                            <Box component="pre" sx={{ fontSize: '0.875rem', overflow: 'auto' }}>
                              {JSON.stringify(log, null, 2)}
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={Math.ceil(pagination.total / pagination.limit)}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Archive All Logs
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to archive all logs?
            {Object.values(filters).some(v => v) && (
              <Typography component="span" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
                Note: This will only archive logs matching your current filters.
              </Typography>
            )}
            <Typography component="span" sx={{ display: 'block', mt: 1 }}>
              Logs will be moved to archive collection and removed from active logs view.
            </Typography>
            <Typography component="span" sx={{ display: 'block', mt: 1, color: 'info.main', fontSize: '0.875rem' }}>
              ⓘ Archived logs are preserved for statistics and can be restored if needed.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAll} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Archiving...' : 'Archive All'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LogsViewer;

